import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Loader2, Check } from "lucide-react";
import { api, BOOK } from "@/lib/book";

const STORAGE_KEY = "12laws_lead_v1";

export default function LeadCaptureModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return; // already shown / submitted
    const t = setTimeout(() => setOpen(true), 8000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissedAt: Date.now() }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setSubmitting(true);
    try {
      await api.post("/leads", { email, source: "homepage_popup" });
      setDone(true);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ submittedAt: Date.now(), email }));
      setTimeout(() => setOpen(false), 1800);
    } catch {
      // Even on error, store so we don't keep nagging
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ erroredAt: Date.now() }));
      setDone(true);
      setTimeout(() => setOpen(false), 1800);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
          onClick={close}
          data-testid="lead-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative grid grid-cols-1 md:grid-cols-5 max-w-3xl w-full bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lead-modal"
          >
            <button
              onClick={close}
              aria-label="Close"
              className="absolute top-3 right-3 z-10 p-2 text-[#525252] hover:text-[#0A0A0A] transition-colors"
              data-testid="lead-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Image side — hidden on mobile */}
            <div className="hidden md:block md:col-span-2 relative bg-[#0A0A0A] overflow-hidden">
              <img
                src={BOOK.cover}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-black/30 to-black/70" />
              <div className="relative p-7 h-full flex flex-col justify-end text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-2">
                  Free download
                </div>
                <div className="font-display font-black text-2xl leading-tight tracking-tight">
                  The 12 Laws,<br />on one page.
                </div>
              </div>
            </div>

            {/* Form side */}
            <div className="md:col-span-3 p-7 sm:p-10">
              {!done ? (
                <>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-3">
                    Inner circle access
                  </div>
                  <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-[0.95]">
                    Get the 12 Laws<br />
                    <span className="text-[#525252]">Reference Card.</span>
                  </h3>
                  <p className="mt-4 text-[#525252] leading-relaxed">
                    Drop your email and we'll send you the printable one-page <span className="text-[#0A0A0A] font-medium">12 Laws Reference Card</span> — plus exclusive notes from Manuel on dating, power, and modern relationships before anyone else sees them.
                  </p>
                  <form onSubmit={submit} className="mt-7 space-y-4" data-testid="lead-form">
                    <div className="relative">
                      <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#525252]" />
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        className="w-full h-14 pl-11 pr-4 bg-white border border-black/15 focus:border-[#0A0A0A] focus:outline-none text-sm"
                        data-testid="lead-email-input"
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-3 bg-[#D92D20] text-white px-6 py-4 font-medium hover:bg-[#B42318] transition-colors disabled:opacity-60"
                      data-testid="lead-submit-btn"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                      Send me the Reference Card
                    </button>
                  </form>
                  <button
                    onClick={close}
                    className="mt-5 text-xs text-[#525252] hover:text-[#0A0A0A] underline"
                    data-testid="lead-skip-btn"
                  >
                    No thanks, I'll keep browsing
                  </button>
                  <p className="mt-6 text-[10px] uppercase tracking-[0.2em] text-[#737373]">
                    No spam. Unsubscribe anytime.
                  </p>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="flex flex-col items-start"
                  data-testid="lead-done"
                >
                  <div className="w-12 h-12 bg-[#D92D20] flex items-center justify-center mb-5">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-display font-black text-3xl tracking-tighter leading-[0.95]">
                    You're in.
                  </h3>
                  <p className="mt-3 text-[#525252] leading-relaxed">
                    Your printable Reference Card is ready below. We'll also send you exclusive notes from Manuel before anyone else sees them.
                  </p>
                  <a
                    href="/the-12-laws-reference-card.pdf"
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-6 py-3.5 font-medium hover:bg-[#D92D20] transition-colors"
                    data-testid="lead-download-pdf-btn"
                  >
                    <Mail className="w-4 h-4" /> Download the Reference Card (PDF)
                  </a>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
